import { JsonDynamicComponentService } from './../json-dynamic-component.service';
import { JsonDynamicFormComponent } from './../json-dynamic-form.component';
import { JsonDynamicFormToken } from './../json-dynamic-form-token';
import { UiControlType } from './../enums/ui-control-type.enum';
import { CommonUtilsService } from './../shared/common-utils.service';
import {
  FormGroup,
  FormArray,
  FormControl,
  AbstractControl,
  Validators,
} from '@angular/forms';
import { Config } from './../models/ui-config';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  AfterViewChecked,
  HostListener,
} from '@angular/core';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import {
  get,
  keys,
  isEqual,
  compact,
  forOwn,
  cloneDeep,
  last,
  map,
  merge,
} from 'lodash';
import { HttpMethods } from '../enums/http-method.enum';
import { firstValueFrom, Subscription, Subject } from 'rxjs';
import { FormControlValueChangedAction } from '../models/form-value-changed-event';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { MatPaginator } from '@angular/material/paginator';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {
  ALLOWED_COLUMN_SUFFIXES,
  PRESIGNED_URL_FILE_SIZE_THRESHOLD_MB,
  SIZE_CONVERSION_METRIC,
  VALID_FLOATING_NUMBER_PATTERN,
} from './../constants/lib-constants';
import { ToastrService } from 'ngx-toastr';
import { FileStatsEvent } from '../models/file-stats';

@Component({
  selector: 'ra-csv',
  templateUrl: './csv.component.html',
  styleUrls: ['./csv.component.scss'],
  standalone: false,
})
export class CsvComponent
  implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked
{
  @ViewChild(MatTable) table: MatTable<any>;
  @ViewChild(JsonDynamicFormToken) configComp: JsonDynamicFormComponent;
  @ViewChild('formEl') formElement: ElementRef<HTMLFormElement>;
  @ViewChild('newColumnMatSelect') matSelect: MatSelect;
  @ViewChild('columnNameMatSelect') columnNameMatSelect: MatSelect;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Input() config: Config;
  @Input() parentConfig: FormGroup;
  @Input() fileSelectObj: FileStatsEvent | null = null;
  showFirstLastButtons = true;
  defaultPageSize = 10;
  pageOptions = [10, 50, 100];
  loading = false;
  data: any;
  dataSource: MatTableDataSource<any>;
  displayedColumns = [];
  /** Full column list before filtering by per-column `conditionExpression`. */
  private allDisplayedColumns: string[] = [];
  /**
   * Columns the framework injected via `conditionExpression` (i.e. not present
   * in the loaded CSV data). Used so we can remove them when the condition
   * flips false, without touching file-sourced columns.
   */
  private autoInjectedColumns = new Set<string>();
  /** Keys reserved by `columnControls` that are not real data columns. */
  private static readonly RESERVED_COLUMN_CONTROL_KEYS = new Set([
    'default',
    'pairedConfig',
  ]);
  formGroup: FormGroup;
  columnName: string;
  csvActionsError: string;
  editIndex: number = null;
  UIControlType = UiControlType;
  uploadError: string = null;
  notSavedError: string = null;
  notSelectedError: string = null;
  uploading = false;
  parentValueChangesSubscription: Subscription;
  parentValues = [];
  _isEditable = true;
  errorMessage;
  delimiter = '|';
  @Input() readOnly: boolean = false;
  invalidControls = {};
  inputId = CommonUtilsService.createGUID();
  onDestroy = new Subject();
  savedData: any;
  isDropdownHidden: boolean = true;
  isColumnNameChangeDropdownHidden: { [key: number]: boolean } = {};
  isStaticColPresent: boolean = true;
  id = CommonUtilsService.createGUID();
  private tabTitle: HTMLElement;
  progress = 0;
  fileUploadStatus: boolean;
  filterCtrl = new FormControl('');
  private subscriptions: Subscription[] = [];
  private allowedColumns = new Set<string>();
  searchTerm = '';
  invalidColumnsError: string = null;
  disableEdit: boolean = false;
  strategyExp = '';
  // Tracks whether the user has successfully saved/uploaded data at least once.
  // Used to gate the empty-dataframe error so it only appears after a save.
  hasSaved: boolean = false;
  get controls() {
    const pageSize: any = this.paginator?.pageSize || this.defaultPageSize;
    const pageIndex = this.paginator?.pageIndex || 0;
    return this.formData.controls.slice(
      pageSize * pageIndex,
      pageSize * pageIndex + pageSize,
    );
  }
  keys = keys;

  get formData() {
    if ('data' in this.formGroup?.controls) {
      return this.formGroup.get('data') as FormArray;
    }
    return new FormArray([]);
  }

  get isEditable() {
    return this.config?.csvConfig?.isEditable !== false;
  }

  get isRowsEditable() {
    return (
      this._isEditable &&
      this.isEditable &&
      this.config?.csvConfig?.editRows !== false
    );
  }

  constructor(
    private jsonDynamicComponentService: JsonDynamicComponentService,
    private cdRef: ChangeDetectorRef,
    private elRef: ElementRef,
    private toastr: ToastrService,
    public commonUtils: CommonUtilsService,
  ) {}

  ngOnInit() {
    if (!this.config?.csvConfig?.hide) {
      this.jsonDynamicComponentService.addCsvControl(this.id, this);
      this.parentValues = this.getParentReloadValues();
      this.registerParentValueChangeSubscription();
      this.formGroup = new FormGroup({});
      this.createCSVFormGroup([], (progress) => {
        this.progress = progress;
      });
      this.setSavedData();
      if (this.config?.csvConfig?.newColumnConfig) {
        // The new-column dropdown is only used by the add-column UI, which is
        // hidden in read-only mode, so fetching its options here is redundant.
        if (!this.readOnly) {
          this.getDropdownValues(this.config?.csvConfig?.newColumnConfig);
        }
        this.registerFilterValueChangeSubscription();
      }
      this.strategyExp = this.config?.csvConfig?.allowNullStaticCols?.strategy;
      this.setAllowedColumns();
    }
  }

  registerFilterValueChangeSubscription() {
    this.subscriptions[this.subscriptions.length] = this.filterCtrl.valueChanges
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe((value) => {
        this.searchTerm = value;
      });
  }

  ngAfterViewInit() {
    this.onConfigChanged(this.configComp?.jsonFormGroup);
    this.cdRef.detectChanges();
    setTimeout(() => {
      this.cdRef.detectChanges();
    });
  }

  ngAfterViewChecked() {
    if (this.fileSelectObj) {
      if (this.fileSelectObj.selectedFile) {
        this.delimiter = this.fileSelectObj?.delimiter;
        this.onFileSelected(this.fileSelectObj?.selectedFile);
      }
      if (this.fileSelectObj?.rowCount) {
        this.disableEdit = this.fileSelectObj.rowCount > 100;
      }
    }
    if (!this.tabTitle) {
      this.setTabTitle();
    }
  }

  setTabTitle() {
    const tabBody = (this.elRef.nativeElement as HTMLElement)?.closest(
      '.mat-mdc-tab-body',
    );
    const tabBodyParent = tabBody?.closest('.mat-mdc-tab-body-wrapper');
    const tabParent = tabBody?.closest('.mat-mdc-tab-group');
    const tabLabels = tabParent?.querySelector('.mat-mdc-tab-labels');
    const tabBodyIndex = Array.from(tabBodyParent?.children || [])?.findIndex(
      (x) => x === tabBody,
    );
    this.tabTitle =
      tabLabels?.children?.[tabBodyIndex]?.querySelector('.tab-title');
  }

  async setAllowedColumns(): Promise<boolean> {
    let result = false;
    try {
      const url = this.commonUtils.getInterpolatedValue(
        this.config?.csvConfig?.newColumnConfig?.valuesUrl,
        {},
      );
      const body = this.commonUtils.getRequestBodyObject(
        this.config?.csvConfig?.newColumnConfig?.requestBody,
        {},
      );
      const response: any = await firstValueFrom(
        this.commonUtils
          .getHttpCall(HttpMethods.Post, url, body)
          .pipe(takeUntil(this.onDestroy)),
      );
      const columns = response?.list_of_columns || [];
      let pairedColumns = [];
      if (this.config?.csvConfig?.newColumnConfig?.addAsPair) {
        if (this.config?.csvConfig?.newColumnConfig?.pairedColPrefix) {
          pairedColumns = map(
            columns,
            (col) =>
              `${this.config.csvConfig.newColumnConfig.pairedColPrefix}${col}`,
          );
        } else if (this.config?.csvConfig?.newColumnConfig?.pairedColSuffix) {
          pairedColumns = map(
            columns,
            (col) =>
              `${col}${this.config.csvConfig.newColumnConfig.pairedColSuffix}`,
          );
        }
      }
      this.allowedColumns = new Set([...columns, ...pairedColumns]);
      result = true;
    } catch (_error) {
      this.allowedColumns = new Set([]);
      result = false;
    }
    this.allowedColumns = new Set([
      ...this.allowedColumns,
      ...(this.config?.csvConfig?.staticCols || []),
    ]);
    return Promise.resolve(result);
  }

  private addErrorClass(_class: string) {
    const accordionTitle = (this.elRef.nativeElement as HTMLElement).closest(
      '.accordion-title',
    );
    const plainTitle = (this.elRef.nativeElement as HTMLElement).closest(
      '.plain-title',
    );
    accordionTitle?.classList?.add(_class);
    this.tabTitle?.classList?.add(_class);
    plainTitle?.classList?.add(_class);
    this.elRef.nativeElement?.classList?.add(_class);
  }

  private removeErrorClass(_class: string) {
    const accordionTitle = (this.elRef.nativeElement as HTMLElement).closest(
      '.accordion-title',
    );
    const plainTitle = (this.elRef.nativeElement as HTMLElement).closest(
      '.plain-title',
    );
    accordionTitle?.classList?.remove(_class);
    if (this.elRef.nativeElement?.classList?.contains(_class)) {
      this.tabTitle?.classList?.remove(_class);
    }
    plainTitle?.classList?.remove(_class);
    this.elRef.nativeElement?.classList?.remove(_class);
  }

  handleNotSavedError(raise: boolean = false) {
    if (raise) {
      this.notSavedError = 'Please save the changes before proceeding.';
      this.formGroup.setErrors({ notSaved: this.notSavedError });
      this.parentConfig.setErrors({ notSaved: this.notSavedError });
      this.config.csvConfig.notSavedError = true;
      if (
        this.config?.control instanceof FormControl ||
        this.config?.control instanceof FormArray ||
        this.config?.control instanceof FormGroup
      ) {
        this.config.control.setErrors({ notSaved: this.notSavedError });
      }
    } else {
      this.notSavedError = null;
      this.formGroup.setErrors(null);
      this.parentConfig.setErrors(null);
      this.config.csvConfig.notSavedError = false;
      if (
        this.config?.control instanceof FormControl ||
        this.config?.control instanceof FormArray ||
        this.config?.control instanceof FormGroup
      ) {
        this.config.control.setErrors(null);
      }
    }
  }

  handleNotSelectedError(raise: boolean = false) {
    if (raise) {
      this.notSelectedError =
        'Unable to fetch the requested CSV or no data is provided';
      this.formGroup.setErrors({ notSelected: this.notSelectedError });
      this.parentConfig.setErrors({ notSelected: this.notSelectedError });
      this.config.csvConfig.notSelectedError = true;
      if (
        this.config?.control instanceof FormControl ||
        this.config?.control instanceof FormArray ||
        this.config?.control instanceof FormGroup
      ) {
        this.config.control.setErrors({ notSelected: this.notSelectedError });
      }
    } else {
      this.notSelectedError = null;
      this.formGroup.setErrors(null);
      this.parentConfig.setErrors(null);
      this.config.csvConfig.notSelectedError = false;
      if (
        this.config?.control instanceof FormControl ||
        this.config?.control instanceof FormArray ||
        this.config?.control instanceof FormGroup
      ) {
        this.config.control.setErrors(null);
      }
    }
  }

  validateSave(removeClass = false) {
    if (!removeClass && !this.isSaveButtonDisabled()) {
      this.addErrorClass(`csv-not-saved-error-${this.id}`);
      this.handleNotSavedError(true);
    } else {
      this.removeErrorClass(`csv-not-saved-error-${this.id}`);
      this.handleNotSavedError();
    }
  }

  validateCSVSelection(removeClass = false) {
    if (!removeClass && this.hasNoCSVSelection()) {
      this.addErrorClass(`csv-no-selection-error-${this.id}`);
      this.handleNotSelectedError(true);
    } else {
      this.removeErrorClass(`csv-no-selection-error-${this.id}`);
      this.handleNotSelectedError();
    }
  }

  getWidth() {
    if (this.formElement) {
      return (
        window.innerWidth -
        this.formElement?.nativeElement?.getBoundingClientRect().left -
        50 +
        'px'
      );
    }
    return '100% !important';
  }

  private isParentReloadValuesChanged() {
    const parentValues = this.getParentReloadValues();
    return !isEqual(this.parentValues, parentValues);
  }

  private getParentReloadValues() {
    const parentValues = [];
    if (this.config?.csvConfig?.parentValueChangeReloadParams) {
      const parentValue = this.parentConfig.value;
      this.config.csvConfig.parentValueChangeReloadParams.forEach((param) => {
        const value = get(parentValue, param);
        parentValues.push(value);
      });
    }
    return parentValues;
  }

  private registerParentValueChangeSubscription() {
    if (this.parentConfig) {
      this.parentValueChangesSubscription?.unsubscribe();
      this.parentValueChangesSubscription =
        this.parentConfig?.valueChanges.subscribe((_data) => {
          if (this.isParentReloadValuesChanged()) {
            this.parentValues = this.getParentReloadValues();
            this.getData(this.getDataValue());
          }
          this.syncConditionalColumns();
        });
    }
  }

  private getDataValue() {
    return {
      ...(this.parentConfig?.value || {}),
      ...(this.configComp?.jsonFormGroup?.value || {}),
    };
  }

  getDropdownValues(config: Config) {
    this.commonUtils.getDropdownValues(config, {}, this.matSelect);
  }

  getData(value?: any) {
    if (this.config.isDependentControl && !this.config.isConditionSatisfied) {
      return;
    }
    this.clearAllErrors();
    const csvConfig = this.config?.csvConfig;
    let downloadURL = csvConfig?.downloadURL;
    downloadURL = this.getInterpolatedValue(downloadURL, value);
    const body = this.commonUtils.getRequestBodyObject(
      csvConfig?.requestBody,
      value,
      csvConfig?.requestBodyStatic,
    );
    if (downloadURL) {
      const httpCall = this.commonUtils.getHttpCall(
        csvConfig?.downloadMethod || HttpMethods.Get,
        downloadURL,
        body,
      );
      if (httpCall) {
        this.loading = true;
        httpCall.pipe(takeUntil(this.onDestroy)).subscribe(
          (data) => {
            this.data = data;
            this.loading = false;
            // Reset save state on fresh data load.
            this.hasSaved = false;
            this.createCSVFormGroup(this.data, (progress) => {
              this.progress = progress;
              this.setSavedData();
              this.validateStaticCols(this.displayedColumns);
            });
            this.handleNotSavedError();
            this.handleNotSelectedError();
          },
          (_err) => {
            this.loading = false;
            this.data = [];
            // Reset save state when data fetch fails.
            this.hasSaved = false;
            this.createCSVFormGroup(this.data, (progress) => {
              this.progress = progress;
              this.setSavedData();
            });
            this.errorMessage = 'Unable to fetch the requested CSV';
          },
        );
      }
    } else {
      // Reset save state when there is no download URL and we initialize empty data.
      this.hasSaved = false;
      this.createCSVFormGroup([], (progress) => {
        this.progress = progress;
        this.setSavedData();
      });
    }
  }

  clearAllErrors() {
    this.errorMessage = null;
    this.csvActionsError = null;
    this.uploadError = null;
  }

  private updateCSVConfigColumnControls(
    column: string,
    isPairedColumn: boolean = false,
  ) {
    if (isPairedColumn) {
      this.config.csvConfig.columnControls[column] =
        JSON.parse(
          JSON.stringify(this.config.csvConfig.columnControls.pairedConfig),
        ) || {};
      if (this.config.csvConfig.columnControls[column].requestBody) {
        this.config.csvConfig.columnControls[column].requestBody['column'] =
          column.split(
            this.config.csvConfig.newColumnConfig?.pairedColPrefix
              ? this.config.csvConfig.newColumnConfig?.pairedColPrefix
              : this.config.csvConfig.newColumnConfig?.pairedColSuffix,
          )[0];
      }
    } else {
      const columnControlsKeys = keys(
        this.config.csvConfig.columnControls,
      ).filter(
        (col) => this.config.csvConfig.columnControls[col].isColRegEx || false,
      );
      const matchingColumn = columnControlsKeys.find((colOrColExpression) =>
        new RegExp(colOrColExpression).test(column),
      );
      if (matchingColumn) {
        this.config.csvConfig.columnControls[column] =
          cloneDeep(this.config.csvConfig.columnControls[matchingColumn]) || {};
      } else {
        this.config.csvConfig.columnControls[column] = merge(
          {},
          cloneDeep(this.config.csvConfig.columnControls.default),
        );
        if (this.config.csvConfig.columnControls?.[column]?.requestBody) {
          this.config.csvConfig.columnControls[column].requestBody['column'] =
            column;
        }
      }
    }
    if (this.config.csvConfig.columnControls[column]) {
      this.config.csvConfig.columnControls[column]['property'] = column;
    }
  }

  /**
   * @function createCSVFormGroup - creates a form group for the data in chunks
   * @param data - data in array format
   * @param progressCallback - A call back function to update the UI after each
   * chunk is processed. Updates with percentage of data processed.
   */
  createCSVFormGroup(data?: any, progressCallback?: (progess: number) => void) {
    const csvConfig = this.config?.csvConfig;
    const fixedColumns = csvConfig?.fixedColumns ? csvConfig.fixedColumns : [];

    // if this.config.csvConfig?.columnControls is not defined, set it with fixedColumns
    if (!this.config.csvConfig?.columnControls) {
      this.config.csvConfig.columnControls = {};
      fixedColumns.forEach((column) => {
        this.config.csvConfig.columnControls[column] = {};
      });
    }
    this.config.csvConfig.columnControls.default =
      this.config.csvConfig.columnControls.default || {};

    const formArray = new FormArray([]);
    this.formGroup = new FormGroup({});
    this.data = data || [];
    this.fileUploadStatus = false;
    if (this.data && this.data.length > 0) {
      const columns = keys(this.data[0]);
      const possiblePrefix = csvConfig?.newColumnConfig?.pairedColPrefix;
      const possibleSuffix = csvConfig?.newColumnConfig?.pairedColSuffix;
      for (const column of columns) {
        const isPairedColumn =
          csvConfig?.newColumnConfig?.addAsPair &&
          (column.includes(possiblePrefix) || column.includes(possibleSuffix));
        if (isPairedColumn || !fixedColumns.includes(column)) {
          this.updateCSVConfigColumnControls(column, isPairedColumn);
        }
      }
      this.setAllDisplayedColumns([...columns, 'actions']);
      const chunkSize = 500;
      let processedCount = 0;
      let chunkIndex = 0;
      const processChunk = () => {
        const start = chunkIndex * chunkSize;
        const end = Math.min((chunkIndex + 1) * chunkSize, this.data.length);
        for (let i = start; i < end; i++) {
          const record = this.data[i];
          const recordFormGroup = new FormGroup({});
          columns.forEach((column, _colIndex) => {
            const recordFormControl = new FormControl(
              record[column],
              this.getValidators(column, i),
            );
            recordFormGroup.addControl(column, recordFormControl);
          });
          formArray.push(recordFormGroup);
        }
        processedCount += end - start;
        chunkIndex++;
        const progress = Math.round((processedCount / this.data.length) * 100);
        this.formGroup.addControl('data', formArray);
        this.setPageLength();
        if (chunkIndex * chunkSize < this.data.length) {
          setTimeout(processChunk, 0);
        } else {
          this.formGroup.addControl('data', formArray);
          this.setPageLength();
          this.fileUploadStatus = true;
        }
        progressCallback(progress);
      };
      processChunk();
    } else {
      this.setAllDisplayedColumns([...fixedColumns, 'actions']);
      this.formGroup.addControl('data', formArray);
      this.setPageLength();
    }
  }

  private setAllDisplayedColumns(columns: string[]) {
    this.allDisplayedColumns = columns;
    this.autoInjectedColumns.clear();
    this.syncConditionalColumns();
  }

  /**
   * Materializes any `columnControl` whose `conditionExpression` is truthy
   * but is missing from the loaded data, and removes previously auto-injected
   * columns when their condition flips false. File-sourced columns are left
   * intact (just hidden via the visibility filter). Always re-runs the
   * visible-column filter at the end.
   */
  private syncConditionalColumns() {
    const columnControls = this.config?.csvConfig?.columnControls;
    if (columnControls && this.allDisplayedColumns?.length) {
      for (const col of Object.keys(columnControls)) {
        if (CsvComponent.RESERVED_COLUMN_CONTROL_KEYS.has(col)) {
          continue;
        }
        const ctrl = columnControls[col];
        if (!ctrl?.conditionExpression) {
          continue;
        }
        const visible = this.isColumnVisible(col);
        const present = this.allDisplayedColumns.includes(col);
        if (visible) {
          if (!present) {
            this.injectConditionalColumn(col);
          } else {
            this.ensureColumnControlsOnAllRows(col);
          }
        } else if (present && this.autoInjectedColumns.has(col)) {
          this.removeConditionalColumn(col);
        }
      }
    }
    this.recomputeVisibleColumns();
  }

  private injectConditionalColumn(col: string) {
    const insertIdx = this.getConditionalColumnInsertIndex(col);
    this.allDisplayedColumns.splice(insertIdx, 0, col);
    this.ensureColumnControlsOnAllRows(col);
    this.autoInjectedColumns.add(col);
  }

  /** Adds missing FormControls for `col` on every row (e.g. rows added while hidden). */
  private ensureColumnControlsOnAllRows(col: string) {
    this.formData.controls.forEach((row: FormGroup, rowIndex: number) => {
      if (!row.get(col)) {
        row.addControl(
          col,
          new FormControl(
            this.getDefaultColumnValue(col, row),
            this.getValidators(col, rowIndex),
          ),
        );
      }
    });
  }

  private removeConditionalColumn(col: string) {
    const idx = this.allDisplayedColumns.indexOf(col);
    if (idx !== -1) {
      this.allDisplayedColumns.splice(idx, 1);
    }
    this.formData.controls.forEach((row: FormGroup) => {
      if (row.get(col)) {
        row.removeControl(col);
      }
    });
    this.autoInjectedColumns.delete(col);
  }

  /**
   * Returns where to splice an injected column into `allDisplayedColumns`,
   * honouring `fixedColumns` ordering when the column is declared there.
   */
  private getConditionalColumnInsertIndex(col: string): number {
    const fixed = this.config?.csvConfig?.fixedColumns || [];
    const actionsIdx = this.allDisplayedColumns.indexOf('actions');
    const fallback =
      actionsIdx === -1 ? this.allDisplayedColumns.length : actionsIdx;
    const fixedIdx = fixed.indexOf(col);
    if (fixedIdx === -1) {
      return fallback;
    }
    for (let i = fixedIdx - 1; i >= 0; i--) {
      const idx = this.allDisplayedColumns.indexOf(fixed[i]);
      if (idx !== -1) {
        return idx + 1;
      }
    }
    for (let i = fixedIdx + 1; i < fixed.length; i++) {
      const idx = this.allDisplayedColumns.indexOf(fixed[i]);
      if (idx !== -1) {
        return idx;
      }
    }
    return fallback;
  }

  private recomputeVisibleColumns() {
    if (!this.allDisplayedColumns?.length) {
      return;
    }
    this.displayedColumns = this.allDisplayedColumns.filter((col) =>
      this.isColumnVisible(col),
    );
  }

  private isColumnVisible(col: string): boolean {
    const expr =
      this.config?.csvConfig?.columnControls?.[col]?.conditionExpression;
    if (!expr) {
      return true;
    }
    return !!this.commonUtils.safeEvaluate(
      this.commonUtils.getInterpolatedValue(
        expr,
        this.parentConfig?.value || {},
      ),
    );
  }

  /** Drops entries keyed by columns whose `conditionExpression` is false. */
  private filterByVisibleColumns<T extends Record<string, any>>(
    obj: T | undefined,
  ): Partial<T> {
    if (!obj) {
      return {} as Partial<T>;
    }
    return Object.entries(obj)
      .filter(([col]) => this.isColumnVisible(col))
      .reduce((acc, [col, val]) => {
        acc[col as keyof T] = val;
        return acc;
      }, {} as Partial<T>);
  }

  private setPageLength() {
    setTimeout(() => {
      if (this.paginator) {
        this.paginator.length = this.formData.controls.length;
      }
    });
  }

  private getValidators(column: string, rowIndex: number) {
    return [
      ...this.getColumnValidators(column),
      ...this.getDynamicValidators(rowIndex, column),
    ];
  }

  private getColumnValidatorsConfig(column: string) {
    return this.config.csvConfig?.columnControls?.[column]?.validators;
  }

  private getColumnDynamicValidatorsConfig(column: string) {
    return this.config.csvConfig?.columnControls?.[column]?.dynamicValidators;
  }

  private getColumnValidators(column: string) {
    const columnValidatorsConfig = this.getColumnValidatorsConfig(column);
    const uiControlType =
      this.config.csvConfig.columnControls?.[column]?.uiControlType;
    let validators = [];
    if (uiControlType === UiControlType.Number) {
      validators.push(Validators.pattern(VALID_FLOATING_NUMBER_PATTERN));
      if (!this.config.csvConfig.columnControls[column].errorMessages) {
        this.config.csvConfig.columnControls[column].errorMessages = {};
      }
      this.config.csvConfig.columnControls[column].errorMessages['pattern'] =
        'Enter valid number';
    }
    validators = [
      ...validators,
      ...CommonUtilsService.getValidators(columnValidatorsConfig),
    ];
    return validators;
  }

  private getDynamicValidators(rowIndex: number, column: string) {
    return [
      (control: AbstractControl) => {
        try {
          const columnDynamicValidatorsConfig =
            this.getColumnDynamicValidatorsConfig(column);
          if (!columnDynamicValidatorsConfig) {
            return null;
          }
          const substituteValue = {
            ...this.getParamsValue(),
            ...this.parentConfig.value,
            __data: this.formGroup?.value?.data || this.data || [],
            __index: rowIndex,
            __value: control.value,
          };
          let isValid = true;
          const validationObj = {};
          forOwn(columnDynamicValidatorsConfig, (validatorExpr, key) => {
            if (
              !this.commonUtils.safeEvaluate(
                this.commonUtils.getInterpolatedValue(
                  validatorExpr,
                  substituteValue,
                ),
              )
            ) {
              isValid = false;
              validationObj[key] = true;
            }
          });
          if (isValid) {
            delete this.invalidControls[column + rowIndex];
            return null;
          } else {
            this.invalidControls[column + rowIndex] = control;
            return validationObj;
          }
        } catch (_e) {
          return null;
        }
      },
    ];
  }

  onFileSelected(event) {
    this.fileSelectObj = null;
    const input = (event.target as HTMLInputElement).files[0];
    const extension = last(input.name.split('.'));
    if (!extension || !['csv', 'parquet'].includes(extension.toLowerCase())) {
      this.csvActionsError = 'Please enter a valid file.';
      (event.target as HTMLInputElement).value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const data = this.csvToArray(text);
      this.disableEdit = data.length > 100;
      this.data = cloneDeep(data.length > 100 ? data.slice(0, 100) : data);
      // Reset save state when a new file is selected; will be set to true on successful upload.
      this.hasSaved = false;
      if (this.config.csvId) {
        this.config.fileName = this.getInterpolatedValue(this.config?.csvId);
      } else {
        this.config.fileName = this.config.property.replace('_CSV', '');
      }
      const toastrTitle = this.config.fileName.replace(/_/g, ' ').toUpperCase();
      if (!this.config?.csvConfig?.skipValidation) {
        const headers = Object.keys(this.data[0]);
        this.validateStaticCols(headers);
        this.validateAllColumns(headers);
      }
      if (!this.config?.csvConfig?.hide) {
        this.progress = 0;
        this.createCSVFormGroup(this.data, (progress) => {
          this.progress = progress;
          if (this.progress == 100 && this.fileUploadStatus) {
            if (!this.csvActionsError && !this.invalidColumnsError) {
              if (this.canUploadUsingPresignedURL(input)) {
                this.showToastrMessage(
                  'Validating and uploading file...',
                  'info',
                  toastrTitle,
                );
                this.updateCSV(extension, input).then(
                  (_res) => {
                    this.showToastrMessage(
                      'File is uploaded successfully.',
                      'success',
                      toastrTitle,
                    );
                  },
                  (_err) => {
                    this.showToastrMessage(
                      'Error in uploading file, please verify.',
                      'error',
                      toastrTitle,
                    );
                  },
                );
              } else {
                this.showToastrMessage(
                  'File is too large to upload, please verify.',
                  'error',
                  toastrTitle,
                );
              }
            } else {
              this.showToastrMessage(
                'Incorrect file was loaded, please verify.',
                'error',
                toastrTitle,
              );
            }
          }
        });
      } else {
        if (this.canUploadUsingPresignedURL(input)) {
          this.showToastrMessage(
            'Validating and uploading file...',
            'info',
            toastrTitle,
          );
          this.updateCSV(extension, input).then(
            (_res) => {
              this.showToastrMessage(
                'File uploaded successfully.',
                'success',
                toastrTitle,
              );
            },
            (_err) => {
              this.showToastrMessage(
                'Error in uploading file, please verify.',
                'error',
                toastrTitle,
              );
            },
          );
        } else {
          this.showToastrMessage(
            'File is too large to upload, please verify.',
            'error',
            toastrTitle,
          );
        }
      }
      (event.target as HTMLInputElement).value = '';
    };

    reader.readAsText(input);
    this.errorMessage = null;
  }

  private showToastrMessage(message: string, type: string, title?: string) {
    if (!this.uploading) {
      this.toastr[type](message, title);
    }
  }

  private canUploadUsingPresignedURL(input: File): boolean {
    const fileSizeInMB =
      input.size / (SIZE_CONVERSION_METRIC * SIZE_CONVERSION_METRIC);
    if (
      !this.csvActionsError &&
      fileSizeInMB <= PRESIGNED_URL_FILE_SIZE_THRESHOLD_MB
    ) {
      return true;
    }

    return false;
  }

  private csvToArray(str, delimiter = ',') {
    delimiter = this.delimiter || delimiter;
    const headers = str.slice(0, str.indexOf('\n')).split(delimiter);
    const rows = compact(str.slice(str.indexOf('\n') + 1).split('\n'));
    const arr = rows.map((row: string) => {
      const values = row.split(delimiter);
      const el = headers.reduce((object, header, index) => {
        object[header?.trim()] = values[index]?.trim();
        return object;
      }, {});
      return el;
    });
    return arr;
  }

  getInterpolatedValue(expr: string, value: any = {}) {
    if (expr) {
      expr = this.commonUtils.getInterpolatedValue(expr, {
        ...value,
        ...this.parentConfig.value,
      });
    }
    return expr;
  }

  onColumnChange(oldName: string, newName: string) {
    this.formData.controls.forEach((fg: FormGroup, index: number) => {
      const value = fg.get(oldName).value;
      fg.removeControl(oldName);
      fg.addControl(
        newName,
        new FormControl(value, this.getValidators(newName, index)),
      );
    });
    const colIndex = this.allDisplayedColumns.indexOf(oldName);
    if (colIndex !== -1) {
      this.allDisplayedColumns.splice(colIndex, 1, newName);
    }
    this.recomputeVisibleColumns();
  }

  onColumnSelect(event) {
    this.filterCtrl.setValue('');
    this.columnName = event.value;
    this.addColumn(0);
    if (this.config?.csvConfig?.newColumnConfig?.addAsPair) {
      if (this.config?.csvConfig?.newColumnConfig?.pairedColPrefix) {
        this.columnName = `${this.config?.csvConfig?.newColumnConfig?.pairedColPrefix}${event.value}`;
      } else if (this.config?.csvConfig?.newColumnConfig?.pairedColSuffix) {
        this.columnName = `${event.value}${this.config?.csvConfig?.newColumnConfig?.pairedColSuffix}`;
      }
      this.addColumn(0, true);
    }
    this.isDropdownHidden = true;
  }

  onColumnNameChange(
    event: MatSelectChange,
    currentColumn: string,
    index: number,
  ) {
    this.filterCtrl.setValue('');
    this.isColumnNameChangeDropdownHidden[index] = true;
    if (this.doesColumnAlreadyExist(event.value, currentColumn)) {
      return;
    }
    const allIndex = this.allDisplayedColumns.indexOf(currentColumn);
    if (allIndex !== -1) {
      this.allDisplayedColumns[allIndex] = event.value;
    }
    this.recomputeVisibleColumns();
    const controls = this.formData.controls;
    controls.forEach((control: FormGroup, _recordIndex: number) => {
      const oldControl = control.get(currentColumn);
      control.addControl(event.value, cloneDeep(oldControl));
      control.removeControl(currentColumn);
    });
    this.validateStaticCols(this.displayedColumns);
    this.validateAllColumns(this.displayedColumns);
  }

  showColumnNameChangeDropdown(index: number) {
    this.isColumnNameChangeDropdownHidden[index] = false;
    const matSelect = document.getElementById(
      'columnNameMatSelect' + index + this.id,
    ) as HTMLElement;
    matSelect.click();
  }

  hideColumnNameChangeDropdown() {
    this.isColumnNameChangeDropdownHidden = {};
  }

  showDropdown() {
    this.isDropdownHidden = false;
    if (!this.matSelect?.panelOpen) {
      this.matSelect.open();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.hideDropdown();
      this.hideColumnNameChangeDropdown();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event): void {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.hideDropdown();
      this.hideColumnNameChangeDropdown();
    }
  }

  hideDropdown() {
    this.isDropdownHidden = true;
    if (this.matSelect?.panelOpen) {
      this.matSelect.close();
    }
  }

  private doesColumnAlreadyExist(
    column: string,
    excludeColumn?: string,
  ): boolean {
    const columns = this.allDisplayedColumns ?? this.displayedColumns;
    if (columns.includes(column) && column !== excludeColumn) {
      this.csvActionsError = 'Column name already exists.';
      return true;
    }
    return false;
  }

  private validateStaticCols(colsToCheck: string[]) {
    if (!this.config?.csvConfig?.staticCols) {
      this.csvActionsError = null;
      return true;
    }

    this.isStaticColPresent = this.config.csvConfig.staticCols.every((col) =>
      colsToCheck.includes(col),
    );

    if (this.isStaticColPresent) {
      this.csvActionsError = null;
    } else {
      const missingCols = this.config.csvConfig.staticCols.filter(
        (col) => !colsToCheck.includes(col),
      );
      this.csvActionsError =
        'Following columns ' +
        missingCols.join(', ') +
        ' are not present, please check them in the data';
    }

    return this.isStaticColPresent;
  }

  addColumn(index?: number, isPairedColumn: boolean = false) {
    index = index ?? 0;
    if (!this.columnName) {
      this.csvActionsError = 'Enter column name.';
      return;
    }
    if (
      this.doesColumnAlreadyExist(this.columnName) ||
      !this.validateStaticCols(this.displayedColumns)
    ) {
      return;
    }
    if (!this.validateAllColumns(this.displayedColumns)) {
      return;
    }
    this.updateCSVConfigColumnControls(this.columnName, isPairedColumn);
    const controls = this.formData.controls;
    controls.forEach((control: FormGroup, recordIndex: number) => {
      control.addControl(
        this.columnName,
        new FormControl(
          this.config?.csvConfig?.columnControls?.default?.defaultValue,
          this.getValidators(this.columnName, recordIndex),
        ),
      );
    });
    this.displayedColumns.splice(index, 0, this.columnName);
    this.columnName = null;
    // Reset 'no selection' and 'empty dataframe' errors upon adding a column
    this.validateCSVSelection(true);
    if (
      this.errorMessage ===
      'Cannot have an empty dataframe, atleast one row is required.'
    ) {
      this.errorMessage = null;
    }
  }

  deleteColumn(index: number) {
    const columnName = this.displayedColumns[index];
    const controls = this.formData.controls;
    controls.forEach((control: FormGroup) => {
      control.removeControl(columnName);
    });
    this.displayedColumns.splice(index, 1);
    this.validateStaticCols(this.displayedColumns);
    this.validateAllColumns(this.displayedColumns);

    // If no data columns remain (only 'actions' column), clear all rows
    if (!this.hasDataColumns()) {
      while (this.formData.length > 0) {
        this.formData.removeAt(0);
      }
      this.editIndex = null;
      this.table?.renderRows();
      this.setPageLength();
      if (
        this.formData.length === 0 &&
        this.formGroup.value.data?.length === 0
      ) {
        // Only show the empty dataframe error after the user has saved/uploaded once
        this.errorMessage = this.hasSaved
          ? 'Cannot have an empty dataframe, atleast one row is required.'
          : null;
      }
    }
  }

  deleteRow(index: number) {
    const deleteIndex =
      index + this.paginator.pageSize * this.paginator.pageIndex;
    this.formData.removeAt(deleteIndex);
    if (this.editIndex === index) this.editIndex = null;
    this.table.renderRows();
    this.setPageLength();
    if (this.uploadError) this.uploadError = null;
    if (this.formData.length === 0 && this.formGroup.value.data?.length === 0) {
      // Only show the empty dataframe error after the user has saved/uploaded once
      this.errorMessage = this.hasSaved
        ? 'Cannot have an empty dataframe, atleast one row is required.'
        : null;
    }
  }

  onEditRow(index: number) {
    this.editIndex = index;
  }

  addRow(rowIndex?: number) {
    const currentControlLength = this.formData.controls.length;
    let addIndex = 0;
    let index: number = 0;
    if (this.paginator) {
      const pageSize = this.paginator?.pageSize;
      if (rowIndex == null) {
        index =
          currentControlLength < pageSize ? currentControlLength : pageSize - 1;
      } else {
        index = rowIndex + 1;
      }
      addIndex = index + this.paginator?.pageSize * this.paginator?.pageIndex;
    }
    const formGroup = new FormGroup({});
    this.displayedColumns.forEach((col: string, _colIndex: number) => {
      if (col !== 'actions') {
        const control = !this.config?.csvConfig?.fixedColumns?.includes(col)
          ? new FormControl(
              this.config?.csvConfig?.columnControls?.default?.defaultValue,
              this.getValidators(col, addIndex),
            )
          : this.config?.csvConfig?.columnControls?.[col]?.defaultValue
            ? new FormControl(
                this.config?.csvConfig?.columnControls?.[col].defaultValue,
                this.getValidators(col, addIndex),
              )
            : this.config?.csvConfig?.columnControls?.[col]
                  ?.defaultValueConditionExpression
              ? new FormControl(
                  this.getDefaultColumnValue(col, formGroup),
                  this.getValidators(col, addIndex),
                )
              : new FormControl(
                  this.formData.controls?.[index - 1]?.get(col)?.value,
                  this.getValidators(col, addIndex),
                );
        formGroup.addControl(col, control);
      }
    });
    this.formData.insert(addIndex, formGroup);
    this.editIndex =
      rowIndex != null ? rowIndex + 1 : this.controls?.length - 1 || 0;
    this.table.renderRows();
    this.setPageLength();
    // Reset 'no selection' and 'empty dataframe' errors upon adding a row
    this.validateCSVSelection(true);
    if (
      this.errorMessage ===
      'Cannot have an empty dataframe, atleast one row is required.'
    ) {
      this.errorMessage = null;
    }
  }

  private getColumnDefaultValueConditionExpression(column: string) {
    return this.config.csvConfig?.columnControls?.[column]
      ?.defaultValueConditionExpression;
  }

  private getDefaultColumnValue(column: string, formGroup: FormGroup) {
    try {
      let defaultValue =
        this.config?.csvConfig?.columnControls?.[column]?.defaultValue || null;
      if (defaultValue) {
        return defaultValue;
      }
      const columnDefaultValueConditionExpression =
        this.getColumnDefaultValueConditionExpression(column);
      if (!columnDefaultValueConditionExpression) {
        return defaultValue;
      }
      const substituteValue = {
        ...this.getParamsValue(),
        ...this.parentConfig.value,
        __data: formGroup || [],
      };
      forOwn(columnDefaultValueConditionExpression, (conditionExpr, key) => {
        if (
          this.commonUtils.safeEvaluate(
            this.commonUtils.getInterpolatedValue(
              conditionExpr,
              substituteValue,
            ),
          )
        ) {
          defaultValue = key;
          return false;
        }
      });
      return defaultValue;
    } catch (_err) {
      return null;
    }
  }

  private getColumnDefaultReadonlyConditionExpression(column: string) {
    return this.config.csvConfig?.columnControls?.[column]
      ?.readOnlyConditionExpression;
  }

  isDisabled(column: string, row: FormGroup) {
    try {
      const conditionExpr =
        this.getColumnDefaultReadonlyConditionExpression(column);
      if (!conditionExpr) {
        return false;
      }
      const substituteValue = {
        ...this.getParamsValue(),
        ...this.parentConfig.value,
        __data: row || [],
      };
      if (
        this.commonUtils.safeEvaluate(
          this.commonUtils.getInterpolatedValue(conditionExpr, substituteValue),
        )
      ) {
        return true;
      }
      return false;
    } catch (_err) {
      return false;
    }
  }

  onSelectionChange(column?: string, element?: FormGroup, rowIndex?: number) {
    if (this.config?.csvConfig?.fixedColumns?.includes(column)) {
      const formGroupCopy = cloneDeep(element);
      this.config?.csvConfig?.fixedColumns.forEach((col) => {
        if (
          col !== 'actions' &&
          this.config?.csvConfig?.columnControls?.[col]
            ?.defaultValueConditionExpression
        ) {
          formGroupCopy
            ?.get(col)
            .setValue(this.getDefaultColumnValue(col, formGroupCopy));
        }
      });
      this.formData.setControl(rowIndex, formGroupCopy);
      this.table.renderRows();
    }
  }

  cloneRow(element?: any, rowIndex?: number) {
    const formGroupCopy = cloneDeep(element);
    const currentControlLength = this.formData.controls.length;
    const pageSize = this.paginator.pageSize;
    let index: number;
    if (rowIndex == null) {
      index =
        currentControlLength < pageSize ? currentControlLength : pageSize - 1;
    } else {
      index = rowIndex + 1;
    }
    const addIndex = index + this.paginator.pageSize * this.paginator.pageIndex;
    this.formData.insert(addIndex, formGroupCopy);
    this.table.renderRows();
    this.setPageLength();
  }

  onConfigChanged(config?: FormGroup) {
    config = config || this.configComp?.jsonFormGroup;
    if (!config || config.valid) {
      this.getData(this.getDataValue());
    }
  }

  getParamsValue() {
    return this.getParamsFormGroup()?.value || {};
  }

  getParamsFormGroup() {
    return this.configComp?.jsonFormGroup;
  }

  validateInvalidControls() {
    forOwn(this.invalidControls, (control: AbstractControl, _key) => {
      control.updateValueAndValidity();
    });
  }

  isColumnValid(column: string) {
    if (this.allowedColumns.has(column)) {
      return true;
    }
    for (const allowedCol of [...this.allowedColumns].sort(
      (a, b) => b.length - a.length,
    )) {
      if (column.startsWith(allowedCol)) {
        const suffix = column.replace(allowedCol, '');
        if (!ALLOWED_COLUMN_SUFFIXES.has(suffix)) {
          return false;
        }
        return true;
      }
    }
    if (this.config?.csvConfig?.isInputData) {
      return false;
    }

    return true;
  }

  validateAllColumns(
    columnsToValidate: string[] = [],
    removeClass: boolean = false,
  ) {
    if (!removeClass) {
      const invalidCols = this.areHeaderColsInvalid(columnsToValidate);
      if (invalidCols && typeof invalidCols !== 'boolean') {
        this.addErrorClass(`csv-invalid-header-cols-error-${this.id}`);
        this.handleInvalidHeaderColsError(true, invalidCols);
        return false;
      }
    } else {
      this.removeErrorClass(`csv-invalid-header-cols-error-${this.id}`);
      this.handleInvalidHeaderColsError();
    }
    return true;
  }

  areHeaderColsInvalid(columnsToValidate: string[]) {
    const colsToCheck = columnsToValidate.filter(
      (col) =>
        col !== 'actions' && !this.config.csvConfig.staticCols?.includes(col),
    );
    const invalidColumns = colsToCheck.filter(
      (col) => !this.isColumnValid(col),
    );
    const validColumns = colsToCheck.filter(
      (col) => !invalidColumns.includes(col),
    );
    if (
      (!invalidColumns.length && !validColumns.length) ||
      (!invalidColumns.length && validColumns.length) ||
      (invalidColumns.length &&
        validColumns.length &&
        this.config?.csvConfig?.isInputData)
    ) {
      return this.validateAllColumns(columnsToValidate, true);
    }

    return invalidColumns;
  }

  handleInvalidHeaderColsError(
    raise: boolean = false,
    invalidColumns: string[] = [],
  ) {
    if (raise) {
      this.invalidColumnsError = `Following columns ${invalidColumns.join(', ')} are not allowed since they are not part of input data.`;
      if (this.config?.csvConfig?.isInputData) {
        this.invalidColumnsError = `Atleast one of ${invalidColumns.join(', ')} columns should be a part of the input data.`;
      } else if (this.config?.csvConfig?.newColumnConfig?.addAsPair) {
        this.invalidColumnsError = `Following columns ${invalidColumns.join(', ')} are invalid.`;
      }
      this.formGroup.setErrors({ invalidColumns: this.invalidColumnsError });
      this.parentConfig.setErrors({ invalidColumns: this.invalidColumnsError });
      this.config.csvConfig.invalidColumnsError = true;
      if (
        this.config?.control instanceof FormControl ||
        this.config?.control instanceof FormArray ||
        this.config?.control instanceof FormGroup
      ) {
        this.config.control.setErrors({
          invalidColumns: this.invalidColumnsError,
        });
      }
    } else {
      this.invalidColumnsError = null;
      this.formGroup.setErrors(null);
      this.parentConfig.setErrors(null);
      this.config.csvConfig.invalidColumnsError = false;
      if (
        this.config?.control instanceof FormControl ||
        this.config?.control instanceof FormArray ||
        this.config?.control instanceof FormGroup
      ) {
        this.config.control.setErrors(null);
      }
    }
  }

  getPresignedURL(uploadURL: string, body) {
    return this.commonUtils.getHttpCall(HttpMethods.Post, uploadURL, body);
  }

  uploadFileViaPresignedURL(preSignedURL: string, body) {
    return this.commonUtils.getHttpCall(HttpMethods.Post, preSignedURL, body);
  }

  updateCSV(filetype: string = 'csv', file?: File): Promise<boolean> {
    if (this.errorMessage) {
      return Promise.resolve(false);
    }
    if (this.uploading) {
      this.uploadError = 'Please wait for the previous upload to complete.';
      return Promise.resolve(false);
    }
    if (
      !this.config?.csvConfig?.skipValidation &&
      !this.validateAllColumns(this.displayedColumns)
    ) {
      return Promise.resolve(false);
    }
    if (!this.config?.csvConfig?.hide && this.isSaveButtonDisabled(file)) {
      return Promise.resolve(true);
    }
    if (!this.config?.csvConfig?.skipValidation) {
      this.validateInvalidControls();
      this.formGroup.markAllAsTouched();
      if (!this.formData.valid) {
        const invalidRowsIndex: number[] = this.getInvalidRowsIndex();
        if (this.paginator) {
          const firstInvalidRow = invalidRowsIndex[0];
          const pageNumber = Math.floor(
            firstInvalidRow / this.paginator.pageSize,
          );
          this.goToPage(pageNumber);
        }
        this.uploadError = 'Please review fields marked in red.';
        return Promise.reject('Validation failed');
      }
      this.jsonDynamicComponentService.emitFormValueChanged({
        childProp: 'data',
        childFormGroup: this.formGroup,
        property: this.config.property,
        formGroup: this.formGroup,
        action: FormControlValueChangedAction.OnUpdateCsv,
        config: this.config,
      });
    }
    const csvConfig = this.config?.csvConfig;
    this.uploadError = null;
    this.uploading = true;
    let uploadUrl = csvConfig?.uploadURL;
    uploadUrl = this.getInterpolatedValue(uploadUrl, this.getParamsValue());
    this.config.fileName = this.getInterpolatedValue(this.config?.csvId);
    const toastrTitle = this.config.fileName.replace(/_/g, ' ').toUpperCase();
    if (csvConfig?.allowNullStaticCols) {
      const nullColumnsAndStrategy = {
        allow_nulls_in: csvConfig?.allowNullStaticCols?.allow_nulls_in,
        strategy:
          this.strategyExp && this.getInterpolatedValue(this.strategyExp),
      };
      csvConfig.allowNullStaticCols = { ...nullColumnsAndStrategy };
    }
    if (file) {
      const body = {
        generatePresignedURL: 'true',
        fileType: filetype,
        allow_null_static_cols: csvConfig?.allowNullStaticCols || null,
        ...this.commonUtils.getRequestBodyObject(
          csvConfig.requestBody,
          this.getParamsValue(),
          csvConfig.requestBodyStatic,
        ),
      };
      return firstValueFrom(this.getPresignedURL(uploadUrl, body)).then(
        (presignedresponse) => {
          const preSignedURL = presignedresponse.presigned_url.url;
          const fields = presignedresponse.presigned_url.fields;
          const formData = Object.entries(fields).reduce(
            (data: FormData, [key, value]) => {
              data.append(key, value as string);
              return data;
            },
            new FormData(),
          );
          formData.append('file', new Blob([file]));
          this.uploadFileViaPresignedURL(preSignedURL, formData).subscribe(
            async () => {
              try {
                const validateRequestUrl = this.getInterpolatedValue(
                  csvConfig?.validateURL,
                  this.getParamsValue(),
                );
                const validateRequestBody = {
                  static_cols: csvConfig?.staticCols || [],
                  dtypes: this.filterByVisibleColumns(csvConfig?.dtypes),
                  allow_null_static_cols:
                    csvConfig?.allowNullStaticCols || null,
                  delimiter: this.delimiter,
                  ...this.commonUtils.getRequestBodyObject(
                    csvConfig.requestBody,
                    this.getParamsValue(),
                    csvConfig.requestBodyStatic,
                  ),
                };
                const res = await firstValueFrom(
                  this.commonUtils
                    .getHttpCall(
                      this.config.csvConfig.uploadMethod || HttpMethods.Post,
                      validateRequestUrl,
                      validateRequestBody,
                    )
                    .pipe(takeUntil(this.onDestroy)),
                );
                res.data_modified && (await this.getData(this.getDataValue()));
                if (!this.config?.csvConfig?.skipValidation) {
                  this.setSavedData();
                  this.validateSave();
                  this.handleNotSelectedError();
                }
                // Mark that a successful save/upload has occurred.
                this.hasSaved = true;
                this.errorMessage = null;
                this.uploading = false;
                this.uploadError = null;
                this.showToastrMessage(res.status, 'success', toastrTitle);
                this.jsonDynamicComponentService.emitFormValueChanged({
                  property: this.config.property,
                  formGroup: this.formGroup,
                  action: FormControlValueChangedAction.CSVUpdated,
                  config: this.config,
                });
              } catch (err) {
                this.errorMessage = null;
                this.uploading = false;
                this.uploadError =
                  err.error.message || err.message || 'Error uploading data.';
                this.showToastrMessage(
                  'File upload failed! Please check in the section for more details.',
                  'error',
                  toastrTitle,
                );
                return Promise.resolve(false);
              }
            },
            (err) => {
              this.errorMessage = null;
              this.uploading = false;
              this.uploadError =
                err.error.message || err.message || 'Error uploading data.';
              this.showToastrMessage(
                'File upload failed! Please check in the section for more details.',
                'error',
                toastrTitle,
              );
              return Promise.resolve(false);
            },
          );
          return Promise.resolve(true);
        },
        (_) => {
          this.errorMessage = null;
          this.uploading = false;
          this.uploadError = 'Error getting Pre signed URL.';
          this.showToastrMessage(
            'File upload failed! Please check in the section for more details.',
            'error',
            toastrTitle,
          );
          return Promise.resolve(false);
        },
      );
    } else {
      this.showToastrMessage(
        'Validating and uploading data...',
        'info',
        toastrTitle,
      );
      const body = {
        ...{
          [csvConfig.csvDataUploadKey || 'data']: (
            this.formGroup.value.data || []
          ).map((row: Record<string, any>) => this.filterByVisibleColumns(row)),
          static_cols: csvConfig?.staticCols || [],
          generatePresignedURL: 'false',
          fileType: filetype,
          dtypes: this.filterByVisibleColumns(csvConfig?.dtypes),
          allow_null_static_cols: csvConfig?.allowNullStaticCols || null,
          delimiter: this.delimiter,
        },
        ...this.commonUtils.getRequestBodyObject(
          csvConfig.requestBody,
          this.getParamsValue(),
          csvConfig.requestBodyStatic,
        ),
      };
      return firstValueFrom(
        this.commonUtils
          .getHttpCall(
            this.config.csvConfig.uploadMethod || HttpMethods.Post,
            uploadUrl,
            body,
          )
          .pipe(takeUntil(this.onDestroy)),
      ).then(
        async (response) => {
          response.data_modified && (await this.getData(this.getDataValue()));
          this.setSavedData();
          this.validateSave();
          this.handleNotSelectedError();
          // Mark that a successful save/upload has occurred.
          this.hasSaved = true;
          this.errorMessage = null;
          this.uploading = false;
          this.uploadError = null;
          this.showToastrMessage(response.status, 'success', toastrTitle);
          this.jsonDynamicComponentService.emitFormValueChanged({
            property: this.config.property,
            formGroup: this.formGroup,
            action: FormControlValueChangedAction.CSVUpdated,
            config: this.config,
          });
          return true;
        },
        (err) => {
          this.errorMessage = null;
          this.uploading = false;
          // display err.error.message if present else display err.message if present
          // else display default error message
          this.uploadError =
            err.error.message || err.message || 'Error uploading data.';
          this.showToastrMessage(
            'Data upload failed! Please check in the section for more details.',
            'error',
            toastrTitle,
          );
          return false;
        },
      );
    }
  }

  private goToPage(pageNumber: number) {
    this.paginator.pageIndex = pageNumber;
    this.paginator.page.emit({
      pageIndex: this.paginator.pageIndex,
      pageSize: this.paginator.pageSize,
      length: this.paginator.length,
    });
  }

  private getInvalidRowsIndex() {
    const invalidRowsIndex: number[] = [];
    this.formData.controls.forEach((formGroup: FormGroup, index: number) => {
      if (!formGroup.valid) {
        invalidRowsIndex.push(index);
      }
    });
    return invalidRowsIndex;
  }

  setSavedData() {
    this.savedData = cloneDeep(this.formGroup.value.data);
  }

  hasNoCSVSelection() {
    const hasNoCSVSelection = this.formGroup.value.data?.length === 0;
    if (!hasNoCSVSelection) {
      this.validateCSVSelection(true);
    }
    return hasNoCSVSelection;
  }

  isSaveButtonDisabled(file?: File) {
    if (this.errorMessage) return false;
    const isSaveButtonDisabled =
      !this.isStaticColPresent || file
        ? false
        : (this.savedData &&
            isEqual(this.savedData, this.formGroup.value.data)) ||
          (this.formData.length === 0 &&
            this.formGroup.value.data?.length === 0) ||
          (!this.fileUploadStatus && this.progress > 0 && this.progress < 100);
    if (isSaveButtonDisabled) {
      this.validateSave(true);
    }
    return isSaveButtonDisabled;
  }

  isColumnsEditable(column?: string) {
    const fixedColumns = this.config?.csvConfig?.fixedColumns || [];
    return (
      this._isEditable &&
      this.isEditable &&
      this.config?.csvConfig?.editColumns !== false &&
      fixedColumns.indexOf(column) === -1
    );
  }

  isRowEditable(rowIndex: number) {
    return this._isEditable && this.isEditable && this.editIndex === rowIndex;
  }

  onRowEditClick(i: number, element: FormGroup) {
    this.editIndex = this.isRowEditable(i) ? null : i;
    for (let control in element?.controls) {
      element.controls[control].updateValueAndValidity();
    }
    if (this.uploadError) this.uploadError = null;
  }

  /**
   * Indicates if there are any data columns present besides the actions column.
   * Used to enable/disable Add Row when no columns exist.
   */
  hasDataColumns(): boolean {
    return (this.displayedColumns || []).some((col) => col !== 'actions');
  }

  ngOnDestroy() {
    this.parentValueChangesSubscription?.unsubscribe();
    this.onDestroy.next(true);
    this.jsonDynamicComponentService.removeCsvControl(this.id);
    this.validateSave(true);
    this.validateCSVSelection(true);
  }
}
